import { usePlatformPrivilege } from '@mews-commander/core';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { LocalizedText, lt, showSuccessAlert, useDialog } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Dialog, IconButton, IconName, Tooltip } from '@optimus-web/core';

export interface CreateOnboardingTasksButtonProps {
    enterpriseId: string;
}

export function CreateOnboardingTasksButton({ enterpriseId }: CreateOnboardingTasksButtonProps) {
    const canManagePlatformProfiles = usePlatformPrivilege('accessAllEnterprises');
    const [dialogProps, openDialog] = useDialog();
    const handleClose = dialogProps.onClose;

    const { mutate, isPending } = useRepositoryMutation('enterprises', 'addOnboardingTasks', {
        onSuccess: () => showSuccessAlert({ description: lt('EnterpriseCreateOnboardingTasksSuccess') }),
        onSettled: handleClose,
    });
    const addOnboardingTasks = () => mutate({ enterpriseId });

    if (!canManagePlatformProfiles) {
        return null;
    }

    return (
        <>
            <IconButtonWithTooltip
                variant="ghost"
                icon={IconName.AI}
                onClick={openDialog}
                tooltip={<LocalizedText textKey={TextKey.EnterpriseCreateOnboardingTasks} />}
                type="button"
            />
            <Dialog
                title={<LocalizedText textKey={TextKey.EnterpriseCreateOnboardingTasksDialogTitle} />}
                loading={isPending}
                footer={{
                    secondary: (
                        <Button onClick={handleClose} variant="tertiary" size="medium" type="button">
                            <LocalizedText textKey={TextKey.Cancel} />
                        </Button>
                    ),
                    primary: (
                        <Button
                            onClick={addOnboardingTasks}
                            variant="primary"
                            size="medium"
                            data-test-id="experimental-onboarding-task-create"
                            type="submit"
                        >
                            <LocalizedText textKey={TextKey.Create} />
                        </Button>
                    ),
                }}
                {...dialogProps}
            >
                <LocalizedText textKey={TextKey.EnterpriseCreateOnboardingTasksDialogContent} />
            </Dialog>
        </>
    );
}

const IconButtonWithTooltip = Tooltip(IconButton);
